// GalleryCard.jsx

import React, { useEffect, useMemo, useRef, useState } from 'react';

// Context
import { useCurrentLanguage } from 'context/LanguageContext';
import { useNodeTypes } from 'context/NodeTypesContext';
import { useTheme } from 'context/ThemeContext';
import { useUser } from 'context/UserContext';

// Services
import { getChildNodesForUser, getNode } from 'services/nodes/NodeReadService';
import { inferStoragePathFromUrl, resolveImageUrl, resolvePlayableVideoUrl } from 'services/storage/StorageAccessService';

// CSS
import './GalleryCard.css';

/**
 * GalleryCard
 *
 * Displays a gallery node as a media grid for feed previews.
 *
 * @param {object} node The gallery node.
 * @param {Function} onClick The click node handler.
 *
 * @returns {ReactNode} GalleryCard component.
 */
const GalleryCard = ({ node, onClick }) => {

  // ----- CONTEXT ----- //

  // Theme Context

  const { theme } = useTheme();

  // Node Types Context
  const { getLabel } = useNodeTypes();

  // Language Context
  const language = useCurrentLanguage();

  // User Context
  const { user } = useUser();

  // ----- STATE ----- //

  // When this card is rendered from a parent summary (e.g., in a POST's attachments),
  // it may not include its own child summaries. Hydrate on-demand so we can preview media.

  const [hydratedNode, setHydratedNode] = useState(null);
  const effectiveNode = hydratedNode || node;

  // ----- MEMOS ----- //

  const hasEmbeddedChildren = useMemo(() => (
    (Array.isArray(node?.childNodes) && node.childNodes.length > 0) ||
    (Array.isArray(node?.children) && node.children.length > 0)
  ), [node?.childNodes, node?.children]);

  // ----- EFFECTS ----- //

  useEffect(() => {
    let cancelled = false;

    const key = node?.key;
    if (!key) {
      setHydratedNode(null);
      return () => {         cancelled = true; };
    }

    // Avoid briefly showing stale media when the component is reused for a new node.
    setHydratedNode((prev) => (prev?.key === key ? prev : null));

    if (hasEmbeddedChildren) {
      setHydratedNode(null);
      return () => {         cancelled = true; };
    }

    (async () => {
      try {
        const full = await getNode(key);
        if (cancelled || !full) return;

        const children = await getChildNodesForUser(key, user || null, full);
        if (cancelled) return;

        setHydratedNode({
          ...full,
          childNodes: Array.isArray(children) ? children : [],
        });
      } catch (err) {
        // Non-fatal: we'll just render without media.
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [hasEmbeddedChildren, node?.key, user]);

  // ----- MEMOS ----- //

  const title = useMemo(() => {
    if (!effectiveNode) return 'Untitled';
    return effectiveNode.title || getLabel(effectiveNode?.type, language) || 'Untitled';
  }, [effectiveNode, getLabel, language]);

  const description = useMemo(() => {
    if (!effectiveNode || !effectiveNode.description) return null;
    return effectiveNode.description;
  }, [effectiveNode]);

  const rawChildren = useMemo(() => {
    if (!effectiveNode) return [];
    const combined = [];
    if (Array.isArray(effectiveNode.childNodes)) combined.push(...effectiveNode.childNodes);
    if (Array.isArray(effectiveNode.children)) combined.push(...effectiveNode.children);
    return combined;
  }, [effectiveNode]);

  const mediaItems = useMemo(() => {
    const items = [];
    const seen = new Set();

    if (Array.isArray(rawChildren) && rawChildren.length > 0) {
      const pickPhotoUrl = (mediaNode) => (typeof mediaNode.url === 'string' ? mediaNode.url : null);
      const pickVideoCover = (mediaNode) =>
        typeof mediaNode.coverUrl === 'string'
          ? mediaNode.coverUrl
          : (typeof mediaNode.url === 'string' ? mediaNode.url : null);

      rawChildren.forEach((child) => {
        const candidate = child;
        if (!candidate) return;

        const mediaType = candidate.type;
        if (mediaType !== 'PHOTO' && mediaType !== 'VIDEO') return;

        const urlRaw = mediaType === 'PHOTO' ? pickPhotoUrl(candidate) : pickVideoCover(candidate);
        const url = typeof urlRaw === 'string' ? urlRaw.trim() : '';
        if (!url) return;

        const dedupeKey = candidate.key || url;
        if (seen.has(dedupeKey)) return;
        seen.add(dedupeKey);

        const nodeKey = candidate.key || null;
        const storagePath = mediaType === 'VIDEO'
          ? (candidate.coverStoragePath || candidate.storagePath || null)
          : (candidate.storagePath || null);

        items.push({
          id: dedupeKey,
          url,
          type: mediaType,
          nodeKey,
          storagePath,
          node: candidate,
        });
      });
    }

    if (items.length === 0 && effectiveNode?.coverUrl) {
      items.push({
        id: 'cover-fallback',
        url: effectiveNode.coverUrl,
        type: 'PHOTO',
        nodeKey: effectiveNode.key || null,
        storagePath: effectiveNode.coverStoragePath || null,
        node: effectiveNode,
      });
    }

    return items;
  }, [effectiveNode, rawChildren]);

  const maxVisible = mediaItems.length === 3 ? 3 : Math.min(mediaItems.length, 4);
  const visibleMedia = mediaItems.slice(0, maxVisible);
  const overflowCount = Math.max(mediaItems.length - maxVisible, 0);
  const gridAddon = visibleMedia.length > 4 ? 4 : visibleMedia.length;
  const gridClassName = ['gallery-card-media-grid', gridAddon ? `gallery-card-media-grid--${gridAddon}` : null]
    .filter(Boolean)
    .join(' ');

  // ----- STATE ----- //

  const [resolvedMediaUrls, setResolvedMediaUrls] = useState({});

  // ----- UTILITIES ----- //

  const isRawStoragePath = (value) => {
    if (!value || typeof value !== 'string') return false;
    return Boolean(inferStoragePathFromUrl(value)) && !String(value).includes('://');
  };

  // ----- EFFECTS ----- //

  useEffect(() => {
    let cancelled = false;

    if (!mediaItems.length) {
      setResolvedMediaUrls({});
      return undefined;
    }

    (async () => {
      try {
        const entries = await Promise.all(
          mediaItems.map(async (media) => {
            const raw = typeof media?.url === 'string' ? media.url : '';
            if (!raw) return [media.id, null];

            if (raw.startsWith('blob:') || raw.startsWith('data:')) return [media.id, raw];

            if (media.type === 'VIDEO') {
              const url = await resolvePlayableVideoUrl(media.node);
              return [media.id, url || raw];
            }

            const url = await resolveImageUrl({
              nodeKey: media.nodeKey || null,
              url: raw,
              storagePath: media.storagePath || null,
            });

            return [media.id, url || raw];
          })
        );

        if (cancelled) return;

        const next = entries.reduce((acc, [id, url]) => {
          if (url) acc[id] = url;
          return acc;
        }, {});

        setResolvedMediaUrls(next);
      } catch (e) {
        if (!cancelled) setResolvedMediaUrls({});
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [mediaItems]);

  // ----- REFS ----- //

  // Track whether this card is visible in the viewport for video autoplay

  const containerRef = useRef(null);

  // ----- STATE ----- //

  const [isVisible, setIsVisible] = useState(false);

  // ----- EFFECTS ----- //

  useEffect(() => {
    const nodeEl = containerRef.current;
    if (!nodeEl || typeof IntersectionObserver === 'undefined') return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.target !== nodeEl) return;
          const shouldPlay = entry.isIntersecting && entry.intersectionRatio > 0.25;
          setIsVisible(shouldPlay);
        });
      },
      {
        root: null,
        rootMargin: '0px 0px 200px 0px',
        threshold: [0, 0.25, 0.5, 0.75, 1],
      }
    );

    observer.observe(nodeEl);

    return () => {
      observer.disconnect();
    };
  }, []);

  // Play/pause any videos inside this card based on visibility
  useEffect(() => {
    const nodeEl = containerRef.current;
    if (!nodeEl) return;

    const videos = nodeEl.querySelectorAll('video.gallery-card-video');
    videos.forEach((videoEl) => {
      if (!(videoEl instanceof HTMLVideoElement)) return;

      if (isVisible) {
        const playPromise = videoEl.play();
        if (playPromise?.catch) {
          playPromise.catch(() => {
            // Autoplay may be blocked; ignore and let user interaction start playback
          });
        }
      } else {
        videoEl.pause();
      }
    });
  }, [isVisible]);

  if (!effectiveNode) return null;

  // ----- RENDER ----- //

  return (
    <div
      className="gallery-card-container"
      ref={containerRef}
      onClick={onClick}
    >
      {visibleMedia.length > 0 && (
        <div className={gridClassName}>
          {visibleMedia.map((media, index) => (
            <div
              className="gallery-card-media-item"
              key={media.id}
            >

              {/* VIDEO */}
              {media.type === 'VIDEO' ? (
                <video
                  className="gallery-card-video"
                  src={resolvedMediaUrls[media.id] || (isRawStoragePath(media.url) ? undefined : media.url)}
                  muted
                  loop
                  playsInline
                  preload="metadata"
                />
              ) : (
                (() => {
                  const resolved = resolvedMediaUrls[media.id] || null;
                  const raw = typeof media?.url === 'string' ? media.url : '';
                  const src = resolved || (isRawStoragePath(raw) ? null : raw);
                  return src ? (
                    <img
                      src={src}
                      alt=""
                      draggable="false"
                      loading="lazy"
                    />
                  ) : null;
                })()
              )}

              {/* MEDIA OVERLAY */}
              {overflowCount > 0 && index === visibleMedia.length - 1 && (
                <span
                  className="gallery-card-media-overlay"
                  style={{
                    color: theme.highlightForeground || theme.foreground,
                  }}
                >
                  +{overflowCount}
                </span>
              )}
            </div>
          ))}
        </div>
      )}

      {/* TITLE */}
      <div
        className="gallery-card-title"
      >
        {title}
      </div>

      {/* DESCRIPTION */}
      {description && (
        <div
          className="gallery-card-description"
        >
          {description}
        </div>
      )}

    </div>
  );
};

// Dev Tools
GalleryCard.displayName = 'GalleryCard';

export default GalleryCard;
